import { usePrincipalKey } from "@auth/sessionStore";
import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  ALL_POLICY_VERSIONS,
  fetchEventCatalog,
  fetchTraceDetail,
  fetchTracePage,
} from "./traceClient";
import type { CatalogScope, TraceSummary } from "./types";

export const fetchOnce = {
  staleTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export const tracePageOptions = (
  principal: string,
  search: string,
  status: TraceSummary["status"] | "",
  cursor: string | undefined,
) =>
  queryOptions({
    queryKey: [
      "event-traces",
      principal,
      "list",
      search,
      status,
      cursor,
    ] as const,
    queryFn: ({ signal }) => fetchTracePage(cursor, search, status, signal),
    ...fetchOnce,
  });

export const useTracePage = (
  active: boolean,
  search: string,
  status: TraceSummary["status"] | "",
  cursor: string | undefined,
) => {
  const principal = usePrincipalKey();
  return useQuery({
    ...tracePageOptions(principal, search, status, cursor),
    enabled: active && principal !== "anon",
  });
};

export const useTraceDetail = (eventUuid: string, active: boolean) => {
  const principal = usePrincipalKey();
  return useQuery({
    queryKey: ["event-traces", principal, "detail", eventUuid],
    queryFn: ({ signal }) => fetchTraceDetail(eventUuid, signal),
    enabled: active && principal !== "anon",
    ...fetchOnce,
  });
};

const catalogKey = (
  principal: string,
  policyVersion: string | undefined,
  windowDays: number,
) =>
  [
    "event-traces",
    principal,
    "catalog",
    policyVersion ?? "",
    windowDays,
  ] as const;

/** The requested version: an explicit one, or `ALL_POLICY_VERSIONS` when the scope includes every version. */
export const scopeVersion = (scope: CatalogScope): string =>
  scope.policy_version ?? ALL_POLICY_VERSIONS;

/**
 * `policyVersion` is undefined only until the server names its current version.
 * That first response is also cached under the explicit version, which the
 * panel then selects, so one scope never has two live cache entries.
 */
export const useEventCatalog = (
  active: boolean,
  policyVersion: string | undefined,
  windowDays: number,
) => {
  const principal = usePrincipalKey();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: catalogKey(principal, policyVersion, windowDays),
    queryFn: async ({ signal }) => {
      const catalog = await fetchEventCatalog(
        policyVersion,
        windowDays,
        signal,
      );
      if (policyVersion === undefined)
        queryClient.setQueryData(
          catalogKey(principal, scopeVersion(catalog.scope), windowDays),
          catalog,
        );
      return catalog;
    },
    enabled: active && principal !== "anon",
    placeholderData: keepPreviousData,
    ...fetchOnce,
  });
};
